'use client'

import { useState, useEffect } from 'react'
import { useTranslation } from 'components/providers/TranslationProvider'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'ui/card'
import { Button } from 'ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'ui/table'
import { Loader2, ArrowLeft } from 'lucide-react'
import LoadingAnimation from 'layout/LoadingAnimation'
import { NormalizedMatch, EaMatch, EaPlayerStats, EaMemberStats } from 'lib/proclubs/shared'
import type { ProclubsClubData } from 'lib/api/proclubs-public'
import ProclubsMatchCard from './ProclubsMatchCard'
import { ProclubsTeamLineup } from './ProclubsTeamLineup'
import ProclubsPlayerStatsCard from './ProclubsPlayerStatsCard'

interface ProclubsTeamDetailProps {
    clubId: string
    clubData?: ProclubsClubData | null // Optional pre-fetched club data (for future optimization)
}

/**
 * ProclubsTeamDetail - Client Component
 *
 * Displays:
 * - Team summary stats
 * - All matches in a table
 * - Player stats for each match
 */
const ProclubsTeamDetail = ({ clubId, clubData: _clubData }: ProclubsTeamDetailProps) => {
    const { t, isLoading: translationsLoading } = useTranslation()
    const [loading, setLoading] = useState(true)
    const [matches, setMatches] = useState<NormalizedMatch[]>([])
    const [rawMatches, setRawMatches] = useState<EaMatch[]>([])
    const [summary, setSummary] = useState<any>(null)
    const [memberStats, setMemberStats] = useState<EaMemberStats[]>([])
    const [positionCount, setPositionCount] = useState<Record<string, number>>({})
    const [selectedMatch, setSelectedMatch] = useState<NormalizedMatch | null>(null)
    const [selectedPlayer, setSelectedPlayer] = useState<EaPlayerStats | null>(null)
    const [error, setError] = useState<string | null>(null)

    if (translationsLoading || !t) {
        return <LoadingAnimation />
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            setError(null)
            try {
                // Fetch matches and member stats in parallel
                const [matchesResponse, membersResponse] = await Promise.all([
                    fetch(`/api/proclubs/matches?clubId=${clubId}&matchType=friendlyMatch`),
                    fetch(`/api/proclubs/members?clubId=${clubId}`)
                ])

                const matchesData = await matchesResponse.json()
                const membersData = await membersResponse.json()

                if (!matchesResponse.ok) {
                    throw new Error(matchesData.error || 'Failed to fetch matches')
                }

                console.log('[ProclubsTeamDetail] Initial data fetch complete:', {
                    matches: matchesData.matches || [],
                    rawMatches: matchesData.rawMatches || [],
                    summary: matchesData.summary,
                    uniqueClubs: matchesData.uniqueClubs || [],
                    memberStats: membersData.members || [],
                    positionCount: membersData.positionCount || {}
                })

                setMatches(matchesData.matches || [])
                setRawMatches(matchesData.rawMatches || [])
                setSummary(matchesData.summary)

                // Set member stats if available (don't fail if members endpoint fails)
                if (membersResponse.ok && membersData.members) {
                    setMemberStats(membersData.members)
                    setPositionCount(membersData.positionCount || {})
                }

                // Cache the club for future name searches
                if (matchesData.matches && matchesData.matches.length > 0) {
                    const clubName = matchesData.matches[0].clubName
                    if (clubName && typeof window !== 'undefined') {
                        try {
                            const cached = localStorage.getItem('proclubs_cached_clubs')
                            const clubs: Array<{ clubId: string; name: string; lastViewed?: number }> = cached
                                ? JSON.parse(cached)
                                : []
                            // Remove existing entry if present
                            const filtered = clubs.filter(c => c.clubId !== clubId)
                            // Add to beginning
                            filtered.unshift({ clubId, name: clubName, lastViewed: Date.now() })
                            // Keep only 100 most recent
                            const trimmed = filtered.slice(0, 100)
                            localStorage.setItem('proclubs_cached_clubs', JSON.stringify(trimmed))
                        } catch (error) {
                            console.error('Failed to cache club:', error)
                        }
                    }
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load team data')
            } finally {
                setLoading(false)
            }
        }

        if (clubId) {
            fetchData()
        }
    }, [clubId])

    // Get raw match data for selected match
    const getRawMatch = (match: NormalizedMatch): EaMatch | null => {
        return rawMatches.find(m => m.matchId === match.matchId) || null
    }

    // Get opponent club ID from match
    const getOpponentClubId = (match: NormalizedMatch): string => {
        return match.opponentClubId
    }

    const rawMatch = selectedMatch ? getRawMatch(selectedMatch) : null
    const opponentClubId = selectedMatch ? getOpponentClubId(selectedMatch) : ''

    // Log all match data when a match is selected
    useEffect(() => {
        if (selectedMatch && rawMatch) {
            console.log('[ProclubsTeamDetail] Selected match data:', {
                normalizedMatch: selectedMatch,
                rawMatch: rawMatch,
                rawMatchClubs: rawMatch.clubs,
                rawMatchPlayers: rawMatch.players,
                rawMatchAggregate: rawMatch.aggregate,
                opponentClubId: opponentClubId,
                homeClubData: rawMatch.clubs[clubId],
                awayClubData: rawMatch.clubs[opponentClubId],
                homePlayers: rawMatch.players?.[clubId] ? Object.values(rawMatch.players[clubId]) : [],
                awayPlayers: rawMatch.players?.[opponentClubId] ? Object.values(rawMatch.players[opponentClubId]) : []
            })
        }
    }, [selectedMatch, rawMatch, clubId, opponentClubId])

    if (loading) {
        return (
            <div className='flex items-center justify-center h-[60dvh]'>
                <Loader2 className='h-8 w-8 animate-spin' />
            </div>
        )
    }

    if (error) {
        return (
            <Card>
                <CardContent className='pt-6'>
                    <p className='text-red-500'>{error}</p>
                </CardContent>
            </Card>
        )
    }

    return (
        <div className='flex flex-col gap-4'>
            {/* Summary Stats */}
            {summary && (
                <Card>
                    <CardHeader>
                        <CardTitle>Team Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                            <div>
                                <div className='text-sm text-muted-foreground'>Played</div>
                                <div className='text-2xl font-bold'>{summary.played}</div>
                            </div>
                            <div>
                                <div className='text-sm text-muted-foreground'>Wins</div>
                                <div className='text-2xl font-bold text-green-600'>{summary.wins}</div>
                            </div>
                            <div>
                                <div className='text-sm text-muted-foreground'>Draws</div>
                                <div className='text-2xl font-bold text-yellow-600'>{summary.draws}</div>
                            </div>
                            <div>
                                <div className='text-sm text-muted-foreground'>Losses</div>
                                <div className='text-2xl font-bold text-red-600'>{summary.losses}</div>
                            </div>
                            <div>
                                <div className='text-sm text-muted-foreground'>Goals For</div>
                                <div className='text-2xl font-bold'>{summary.goalsFor}</div>
                            </div>
                            <div>
                                <div className='text-sm text-muted-foreground'>Goals Against</div>
                                <div className='text-2xl font-bold'>{summary.goalsAgainst}</div>
                            </div>
                            <div>
                                <div className='text-sm text-muted-foreground'>Goal Difference</div>
                                <div
                                    className={`text-2xl font-bold ${summary.goalDiff >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                >
                                    {summary.goalDiff > 0 ? '+' : ''}
                                    {summary.goalDiff}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Member Stats */}
            {memberStats.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle>Team Members</CardTitle>
                        {Object.keys(positionCount).length > 0 && (
                            <CardDescription>
                                {Object.entries(positionCount)
                                    .map(([pos, count]) => `${count} ${pos}`)
                                    .join(', ')}
                            </CardDescription>
                        )}
                    </CardHeader>
                    <CardContent>
                        <div className='overflow-x-auto'>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className='min-w-[150px]'>Name</TableHead>
                                        <TableHead className='w-[60px] text-center'>GP</TableHead>
                                        <TableHead className='w-[80px] text-center'>Win %</TableHead>
                                        <TableHead className='w-[70px] text-center'>Goals</TableHead>
                                        <TableHead className='w-[70px] text-center'>Assists</TableHead>
                                        <TableHead className='w-[70px] text-center'>Rating</TableHead>
                                        <TableHead className='w-[100px] text-center'>Position</TableHead>
                                        <TableHead className='w-[80px] text-center'>Overall</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {memberStats
                                        .filter(member => parseInt(member.gamesPlayed) > 0) // Only show members who have played
                                        .sort((a, b) => parseInt(b.gamesPlayed) - parseInt(a.gamesPlayed)) // Sort by games played
                                        .map((member, index) => (
                                            <TableRow key={`${member.name}-${index}`}>
                                                <TableCell className='font-medium'>{member.name}</TableCell>
                                                <TableCell className='text-center'>{member.gamesPlayed}</TableCell>
                                                <TableCell className='text-center'>{member.winRate}%</TableCell>
                                                <TableCell className='text-center'>{member.goals}</TableCell>
                                                <TableCell className='text-center'>{member.assists}</TableCell>
                                                <TableCell className='text-center'>
                                                    {parseFloat(member.ratingAve || '0').toFixed(1)}
                                                </TableCell>
                                                <TableCell className='text-center capitalize'>
                                                    {member.favoritePosition || 'N/A'}
                                                </TableCell>
                                                <TableCell className='text-center font-semibold'>
                                                    {member.proOverall}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Match Detail View */}
            {selectedMatch && rawMatch ? (
                <div className='flex flex-col gap-4'>
                    <Button
                        variant='ghost'
                        onClick={() => {
                            setSelectedMatch(null)
                            setSelectedPlayer(null)
                        }}
                        className='w-fit'
                    >
                        <ArrowLeft className='mr-2 h-4 w-4' />
                        Back to Matches
                    </Button>

                    {/* Match Card */}
                    <Card>
                        <ProclubsMatchCard
                            match={selectedMatch}
                            clubId={clubId}
                            onClick={() => {}} // Already selected, no action needed
                            rawMatch={rawMatch}
                        />
                    </Card>

                    {/* Lineups */}
                    <Card className='pt-6'>
                        <CardContent>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full'>
                                <ProclubsTeamLineup
                                    clubId={clubId}
                                    clubName={selectedMatch.clubName || clubId}
                                    rawMatch={rawMatch}
                                    onPlayerClick={setSelectedPlayer}
                                />
                                <ProclubsTeamLineup
                                    clubId={opponentClubId}
                                    clubName={selectedMatch.opponentName || opponentClubId}
                                    rawMatch={rawMatch}
                                    onPlayerClick={setSelectedPlayer}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            ) : (
                /* Matches List */
                <div className='flex flex-col gap-3'>
                    {matches.map(match => {
                        const rawMatchForCard = rawMatches.find(m => m.matchId === match.matchId) || null
                        return (
                            <Card key={match.matchId}>
                                <ProclubsMatchCard
                                    match={match}
                                    clubId={clubId}
                                    onClick={() => {
                                        console.log('[ProclubsTeamDetail] Match clicked:', {
                                            selectedMatch: match,
                                            matchId: match.matchId,
                                            clubId: match.clubId,
                                            opponentClubId: match.opponentClubId,
                                            goalsFor: match.goalsFor,
                                            goalsAgainst: match.goalsAgainst,
                                            playedAt: match.playedAt,
                                            timestamp: match.timestamp
                                        })
                                        setSelectedMatch(match)
                                    }}
                                    rawMatch={rawMatchForCard}
                                />
                            </Card>
                        )
                    })}
                </div>
            )}

            {/* Player Stats Dialog */}
            {selectedPlayer && (
                <ProclubsPlayerStatsCard player={selectedPlayer} onClose={() => setSelectedPlayer(null)} />
            )}
        </div>
    )
}

export default ProclubsTeamDetail
